import {Suspense, type PropsWithChildren} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {ForYouItemError, ForYouItemSkeleton} from './ForYouItemFallbacks'
import {useLayoutAuthorLogin} from '../../../../stores/layout-selectors'
import {useAssigneesQuery} from '../../data/route-queries'

export type ForYouAudience = 'author' | 'reviewer' | 'author-or-assignee'

/** Whether the current viewer is the pull request author. */
export function useViewerIsPullRequestAuthor(): boolean {
  const authorLogin = useLayoutAuthorLogin()
  const viewerLogin = getCurrentUserLogin()
  return !!viewerLogin && authorLogin === viewerLogin
}

/**
 * Whether the current viewer is assigned to the pull request. Reads the overview
 * route's assignees query, so this suspends — only call it from a gate that needs it.
 */
export function useViewerIsPullRequestAssignee(): boolean {
  const {data: assigneesData} = useAssigneesQuery()
  const viewerLogin = getCurrentUserLogin()
  return !!viewerLogin && (assigneesData.assignees ?? []).some(assignee => assignee.displayLogin === viewerLogin)
}

/**
 * Error boundary + suspense wrapper for a single ForYou item. When `audience` is
 * provided, the item only renders for the matching viewer; otherwise it always
 * renders. Audience gating is delegated to per-audience children so that the
 * (suspense-fetching) assignee lookup is only mounted for `author-or-assignee`.
 */
export function ForYouItemBoundary({
  children,
  name,
  audience,
}: PropsWithChildren<{name: string; audience?: ForYouAudience}>) {
  const boundary = (
    <ErrorBoundary boundaryName={`ForYouItem.${name}`} fallback={<ForYouItemError />}>
      <Suspense fallback={<ForYouItemSkeleton />}>{children}</Suspense>
    </ErrorBoundary>
  )

  switch (audience) {
    case undefined:
      return boundary
    case 'author-or-assignee':
      return <AuthorOrAssigneeGate>{boundary}</AuthorOrAssigneeGate>
    default:
      return <AudienceGate audience={audience}>{boundary}</AudienceGate>
  }
}

/** Gates on author vs. everyone else — reads the layout store only (no fetch). */
function AudienceGate({audience, children}: PropsWithChildren<{audience: 'author' | 'reviewer'}>) {
  const viewerIsAuthor = useViewerIsPullRequestAuthor()
  const matchesAudience = audience === 'author' ? viewerIsAuthor : !viewerIsAuthor

  return matchesAudience ? <>{children}</> : null
}

/** Gates on author OR assignee. Isolated so its assignee fetch only runs for this audience. */
function AuthorOrAssigneeGate({children}: PropsWithChildren) {
  const viewerIsAuthor = useViewerIsPullRequestAuthor()
  const viewerIsAssignee = useViewerIsPullRequestAssignee()

  return viewerIsAuthor || viewerIsAssignee ? <>{children}</> : null
}
